// ─────────────────────────────────────────────────────────────────
// certRecommendation.js
//
// Goal-driven AND experience-aware certification recommendation. Each role goal
// (Step 3 `career_goal` key) maps to an ordered cert ladder (foundation →
// target) and a per-cert rationale that explains WHY each credential was chosen
// for that role. The learner's experience (Step 4) decides where in that ladder
// they start: a complete beginner starts at the foundation; someone already
// working in IT can skip ahead to the role's real entry cert.
//
// Startable tracks (have seeded curriculum / are selectable today):
//   comptia_aplus, comptia_network, comptia_security, az_900, az_104.
// Coming-soon tracks (ab_900, ms_102) NEVER appear as the START cert — they sit
// further down the ladder as the "vision," so expStart only ever points at a
// startable track.
// ─────────────────────────────────────────────────────────────────

// Per role:
//   ladder   — ordered track ids, foundation → target (the certs that directly
//              correlate to this role).
//   expStart — index a learner who already works in IT starts at (beginners
//              always start at 0). MUST land on a startable track.
//   summary  — one line on why this path fits the role.
//   why      — per-cert rationale (keyed by track id) shown on the recommendation.
const RECS = {
  help_desk: {
    ladder: ['comptia_aplus', 'comptia_network'],
    expStart: 0,
    summary: 'Help desk roles hire on proven fundamentals — this is the path that gets you screened in.',
    why: {
      comptia_aplus: 'Nearly every help desk posting lists CompTIA A+ by name — it proves you can troubleshoot hardware, operating systems, and everyday user problems, which is the job itself.',
      comptia_network: 'Network+ moves you past first-line support: understanding the networks behind the tickets opens higher-paying support and junior admin roles.',
    },
  },
  desktop_support: {
    ladder: ['comptia_aplus', 'comptia_network'],
    expStart: 0,
    summary: 'Desktop support is an A+ job at its core — this path proves you can do it and grow.',
    why: {
      comptia_aplus: 'Desktop support is hands-on hardware, OS, and peripheral troubleshooting — exactly the ground CompTIA A+ certifies, so it is the credential employers screen for.',
      comptia_network: 'Network+ broadens you beyond the desktop, which is how desktop-support techs step up into systems and infrastructure roles.',
    },
  },
  sysadmin: {
    ladder: ['comptia_aplus', 'comptia_network', 'comptia_security'],
    expStart: 1,
    summary: 'System administration rewards breadth — fundamentals, networking, then security.',
    why: {
      comptia_aplus: 'A+ locks in the hardware and OS fundamentals every administrator is assumed to already know.',
      comptia_network: 'Sysadmins manage networked services daily — Network+ proves you understand the infrastructure you will be configuring.',
      comptia_security: 'Security+ is increasingly required for admin roles; it shows you can keep the systems you manage secure and is a common HR checkbox.',
    },
  },
  network_engineer: {
    ladder: ['comptia_aplus', 'comptia_network', 'comptia_security'],
    expStart: 1,
    summary: 'Network roles are built on Network+ — with security to widen your options.',
    why: {
      comptia_aplus: 'A+ gives the hardware and OS baseline before you specialize in networking.',
      comptia_network: 'Network+ is the direct, vendor-neutral credential for network roles — protocols, infrastructure, and troubleshooting are the core of the work.',
      comptia_security: 'Network engineers are expected to secure what they build; Security+ rounds out the path and opens more postings to you.',
    },
  },
  cloud_engineer: {
    ladder: ['comptia_aplus', 'az_900', 'az_104'],
    expStart: 1,
    summary: 'Cloud roles run on Azure credentials — fundamentals first, then hands-on administration.',
    why: {
      comptia_aplus: 'A+ establishes the IT fundamentals cloud work still rests on.',
      az_900: 'AZ-900 is the recognized entry point into Microsoft Azure — it proves you understand core cloud concepts, services, and pricing.',
      az_104: 'AZ-104 is the credential cloud/Azure administrator roles actually require: hands-on compute, storage, networking, and identity.',
    },
  },
  security_analyst: {
    ladder: ['comptia_aplus', 'comptia_network', 'comptia_security'],
    expStart: 2,
    summary: 'Security analyst postings list Security+ by name — with the foundation that makes it stick.',
    why: {
      comptia_aplus: 'A+ builds the systems foundation before you move into security.',
      comptia_network: 'You cannot secure what you do not understand — Network+ proves the networking knowledge every analyst leans on.',
      comptia_security: 'Security+ is the entry credential security analyst and SOC roles ask for by name.',
    },
  },
  it_manager: {
    ladder: ['comptia_aplus', 'comptia_network', 'comptia_security'],
    expStart: 0,
    summary: 'Leading an IT team takes technical breadth — this path builds it.',
    why: {
      comptia_aplus: 'A+ gives you the technical literacy to lead a support team credibly.',
      comptia_network: 'Network+ broadens your understanding of the infrastructure your team supports.',
      comptia_security: 'Security+ adds the risk and security vocabulary expected of IT leaders.',
    },
  },
  modern_workplace: {
    ladder: ['comptia_aplus', 'ab_900', 'ms_102'],
    expStart: 0,
    summary: 'Microsoft 365 roles are built on the M365 administration path.',
    why: {
      comptia_aplus: 'A+ grounds you in the IT fundamentals behind any Microsoft 365 environment.',
      ab_900: 'AB-900 introduces Microsoft 365 Copilot and AI fundamentals — the direction modern-workplace roles are heading.',
      ms_102: 'MS-102 is the Microsoft 365 Administrator credential these roles are built on: identity, security, and workload management across M365.',
    },
  },
  unsure: {
    ladder: ['comptia_aplus', 'comptia_network'],
    expStart: 0,
    summary: "Not sure yet? Start broad with A+ and keep every door open.",
    why: {
      comptia_aplus: 'When you are not sure yet, A+ is the safest first move — it is the foundation for almost every IT path and keeps your options open.',
      comptia_network: 'Network+ is the natural next step that branches toward support, networking, or security.',
    },
  },
};

// Complete beginners start at the foundation (index 0). Learners with some
// experience (or already working in IT) skip ahead to the role's real entry
// cert (expStart) — which is always a startable track.
export function recommendCert(careerGoalKey, experience = 'beginner') {
  const r = RECS[careerGoalKey] || RECS.unsure;
  const startIdx = experience === 'beginner' ? 0 : Math.min(r.expStart || 0, r.ladder.length - 1);
  const chain = r.ladder.slice(startIdx);
  const start = chain[0];

  return {
    activeTrackId:   start,                       // current_track / active_track_id — always startable
    startCert:       start,                       // chain[0] in every case
    chain,                                        // ordered track ids for the visual path
    intendedTrackId: chain[chain.length - 1],     // the "vision" destination
    summary:         r.summary,                   // why this path fits the role
    why:             r.why,                        // per-cert rationale (keyed by track id)
  };
}
